import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { format } from 'date-fns';
import csvDownload from 'json-to-csv-export';
import { useClientUser } from '@/hooks';
import { UserContext } from '@/contexts/UserContext';
import {
    QUERY_CARRIER_STATUSES,
    QUERY_CARRIER_REGIONS,
    QUERY_CLIENTS,
    QUERY_DELIVERY_PROFILES,
    UPSERT_DELIVERY_PROFILE,
    SYNC_ASSIGNMENT_ZIP_TYPE,
    GET_DELIVERY_PROFILE,
    CREATE_SUBREGION,
    UPLOAD_SUBREGIONS,
    QUERY_DELIVERY_PROFILE_ASSIGNMENTS,
    APPLY_DELIVERY_PROFILE_ASSIGNMENTS,
} from './graphql';
import { DELETE_SUBREGION, INSERT_ZIP_MAPPINGS, DELETE_ZIP_MAPPINGS, UPDATE_SUBREGION } from '../graphql/mutations';
import { GET_CORP_PRICING_OVERRIDES } from '@/components/CorporateUsers/graphql';
import {
    applyOrderTypesFor,
    buildAssignmentRows,
    collapseOrderTypes,
    exclusiveOrderTypes,
    hasMixedZipTypes,
    requirementsForCommodities,
    routedZipType,
    toFormOrderTypes,
    validateProfileForm,
    validRequirementsForCommodities,
    carrierRegionZips,
    carrierStatusesById,
    profileInScope,
    filterCarriersByLeg,
    parseImportedSubregions,
    serializeSubregionsForExport,
} from './utils';
import { mergeInsertedZips } from '../utils';
import { useSaveSubregion } from '../panel/hooks';
import { useColumns } from './constants';

export const useRoleScope = () => {
    const { isOnwardAdmin, isCorporateAdmin, organization_id } = useClientUser();
    return useMemo(() => {
        if (isOnwardAdmin) return { kind: 'onward' };
        if (isCorporateAdmin) return { kind: 'corp', org_id: organization_id };
        return { kind: 'none' };
    }, [isOnwardAdmin, isCorporateAdmin, organization_id]);
};

// null when the scope can't query anything: no admin role, or a corp admin without an organization.
export const whereForScope = (scope) => {
    if (scope.kind === 'onward') return { organization_id: { _is_null: true } };
    if (scope.kind === 'corp' && scope.org_id) return { organization_id: { _eq: scope.org_id } };
    return null;
};

// The scope's profiles, for the profiles grid. Refetched on every visit: subregion edits on a profile
// page change its subregion count, and they don't update this list.
export const useDeliveryProfiles = (scope, { onError } = {}) => {
    const where = whereForScope(scope);

    const { data, loading } = useQuery(QUERY_DELIVERY_PROFILES, {
        variables: { where: where || {} },
        skip: !where,
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        onError: (e) => {
            captureException(e);
            onError?.('Failed to load delivery profiles');
        },
    });

    // Loading only until there's a list to show; the cached one stays up while it refreshes.
    return { deliveryProfiles: data?.results || [], loading: loading && !data };
};

// Creates or edits a profile, and keeps the profiles grid's cached list current.
export const useUpsertDeliveryProfile = (scope, { onError } = {}) => {
    const where = whereForScope(scope);

    const [upsertMutation] = useMutation(UPSERT_DELIVERY_PROFILE, {
        onError: (e) => {
            captureException(e);
            onError?.('Failed to save delivery profile');
        },
        update: (cache, { data: { result } }) => {
            // Only a list that's already cached. Writing one that isn't would seed the grid with
            // just this profile, and the grid would read that instead of fetching.
            cache.updateQuery({ query: QUERY_DELIVERY_PROFILES, variables: { where } }, (data) => {
                if (!data) return undefined;
                const list = data.results || [];
                const idx = list.findIndex((p) => p.delivery_profile_id === result.delivery_profile_id);
                if (idx === -1) return { ...data, results: [...list, result] };
                const next = [...list];
                next[idx] = { ...next[idx], ...result };
                return { ...data, results: next };
            });
        },
    });

    // autoAssign reads the assignment's copy, so a profile edit has to push the value down or
    // orders keep routing off the old zip -- silently.
    const [syncAssignmentZipType] = useMutation(SYNC_ASSIGNMENT_ZIP_TYPE, {
        // The profile queries carry auto_assignments for routedZipType, and neither type is
        // normalized, so the returned rows can't reach them through the cache.
        refetchQueries: ['GetDeliveryProfiles', 'GetDeliveryProfile'],
        onError: (e) => {
            captureException(e);
            onError?.('Saved the profile, but failed to update its shipper assignments');
        },
        update: (cache, { data: { result } }) => {
            const returned = result?.returning || [];
            if (!returned.length) return;
            const variables = { where: { delivery_profile: where || {} } };
            cache.updateQuery({ query: QUERY_DELIVERY_PROFILE_ASSIGNMENTS, variables }, (data) => {
                const list = data?.results || [];
                const byId = new Map(list.map((a) => [a.auto_assign_id, a]));
                returned.forEach((a) => byId.set(a.auto_assign_id, a));
                return { ...(data || {}), results: Array.from(byId.values()) };
            });
        },
    });

    // `previousAssignZipType` is the zip type the form started from -- routedZipType, what the
    // shippers route on -- needed to decide whether the fan-out below should run at all.
    // Resolves with `syncFailed` when the profile saved but the fan-out didn't, so the caller can
    // keep the form open and let a second save retry it.
    return useCallback(
        (
            {
                delivery_profile_id,
                name,
                assign_zip_type,
                commodity,
                delivery_types,
                special_requirements,
                default_order_types,
            },
            { previousAssignZipType } = {}
        ) => {
            // Only the fields the caller actually supplied. An undefined value is dropped rather
            // than sent, so it cannot become an update_column -- see UPSERT_DELIVERY_PROFILE for
            // why a hardcoded column list wipes whatever was omitted.
            const base = Object.fromEntries(
                Object.entries({
                    name,
                    assign_zip_type,
                    commodity,
                    delivery_types,
                    special_requirements,
                    default_order_types,
                }).filter(([, value]) => value !== undefined)
            );
            const update_columns = Object.keys(base);
            const delivery_profile = delivery_profile_id
                ? { delivery_profile_id, ...base }
                : scope.kind === 'corp'
                ? { ...base, organization_id: scope.org_id }
                : base;
            return upsertMutation({ variables: { delivery_profile, update_columns } }).then((res) => {
                const saved = res?.data?.result;
                if (!saved?.delivery_profile_id || !delivery_profile_id || !assign_zip_type) return res;

                // Only when the value differs from what the shippers route on. Compared against the
                // assignments rather than delivery_profiles.assign_zip_type: that column can hold a
                // stale 'DROPOFF' default, and after a failed fan-out it holds the new value while
                // the assignments keep the old one -- either way a check against it skips a fan-out
                // that's needed. A rename leaves the value equal, so it never rewrites the shippers.
                if (!previousAssignZipType || previousAssignZipType === assign_zip_type) return res;

                return syncAssignmentZipType({
                    variables: { delivery_profile_id: saved.delivery_profile_id, zip_type: assign_zip_type },
                }).then((syncRes) => (syncRes?.data?.result ? res : { ...res, syncFailed: true }));
            });
        },
        [scope.kind, scope.org_id, upsertMutation, syncAssignmentZipType]
    );
};

const EMPTY_PROFILE_FORM = {
    name: '',
    assign_zip_type: 'DROPOFF',
    commodity: [],
    delivery_types: [],
    special_requirements: [],
    default_order_types: [],
};

// The profile modal's form: hydration on open, validation, field updates and save.
export const useDeliveryProfileForm = ({ open, deliveryProfile, upsertDeliveryProfile, onClose }) => {
    const isEdit = !!deliveryProfile?.delivery_profile_id;
    const [form, setForm] = useState(EMPTY_PROFILE_FORM);
    const [saving, setSaving] = useState(false);
    const [showErrors, setShowErrors] = useState(false);

    // What the shippers route on, so it is also what a save's fan-out compares against.
    const startingZipType = routedZipType(deliveryProfile);

    useEffect(() => {
        if (open) {
            setForm(
                deliveryProfile
                    ? {
                          name: deliveryProfile.name || '',
                          // The assignments' value when they agree, since the column can be a stale
                          // default. No further fallback: the column is NOT NULL, so fabricating one
                          // would only mask a real value and make a stale default look like a choice.
                          assign_zip_type: startingZipType,
                          // Empty when the profile has none, so validation forces a choice.
                          commodity: deliveryProfile.commodity || [],
                          delivery_types: deliveryProfile.delivery_types || [],
                          // Filtered: a stored value can fall out of the provisional map, and would
                          // then render nowhere yet still be re-persisted.
                          special_requirements: validRequirementsForCommodities(
                              deliveryProfile.special_requirements,
                              deliveryProfile.commodity
                          ),
                          default_order_types: toFormOrderTypes(deliveryProfile.default_order_types),
                      }
                    : EMPTY_PROFILE_FORM
            );
            setShowErrors(false);
            setSaving(false);
        }
    }, [open, deliveryProfile?.delivery_profile_id]);

    const errors = useMemo(() => validateProfileForm(form), [form]);
    const availableRequirements = useMemo(() => requirementsForCommodities(form.commodity), [form.commodity]);

    const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    // Drops only the requirements the new commodities no longer offer, so none stays attached to
    // the wrong freight while shared ones survive.
    const onCommodityChange = (commodity) =>
        setForm((prev) => ({
            ...prev,
            commodity,
            special_requirements: validRequirementsForCommodities(prev.special_requirements, commodity),
        }));

    // Mutual exclusion matters here, not just in the apply bar: without it "All" and a specific type
    // both stay ticked, then collapseOrderTypes discards the specific one on save with no indication.
    const onOrderTypesChange = (orderTypes) =>
        setForm((prev) => ({
            ...prev,
            default_order_types: exclusiveOrderTypes(prev.default_order_types, orderTypes),
        }));

    const toggleInArray = (field, value) =>
        setForm((prev) => {
            const current = prev[field] || [];
            return {
                ...prev,
                [field]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
            };
        });

    const save = async () => {
        if (Object.keys(errors).length) {
            setShowErrors(true);
            return;
        }

        setSaving(true);
        try {
            const res = await upsertDeliveryProfile(
                {
                    delivery_profile_id: deliveryProfile?.delivery_profile_id,
                    ...form,
                    name: form.name.trim(),
                    default_order_types: collapseOrderTypes(form.default_order_types),
                },
                { previousAssignZipType: startingZipType }
            );
            // Apollo resolves rather than throws when onError is supplied, so closing
            // unconditionally would discard the whole form behind an error toast. A failed fan-out
            // stays open too, so Save retries it.
            if (res?.data?.result?.delivery_profile_id && !res.syncFailed) onClose();
        } finally {
            setSaving(false);
        }
    };

    const errorFor = (field) => (showErrors ? errors[field] : undefined);

    return {
        isEdit,
        form,
        saving,
        errorFor,
        availableRequirements,
        mixedZipTypes: hasMixedZipTypes(deliveryProfile),
        setField,
        onCommodityChange,
        onOrderTypesChange,
        toggleInArray,
        save,
    };
};

// The profile a profile page shows, by the id in its URL. delivery_profiles_by_pk isn't scoped (the
// select permission is unfiltered), so a profile from another scope reads as not found.
export const useDeliveryProfileSelection = (scope, deliveryProfileId, { onError } = {}) => {
    const { data, loading, error } = useQuery(GET_DELIVERY_PROFILE, {
        variables: { delivery_profile_id: deliveryProfileId },
        skip: !deliveryProfileId,
        onError: (e) => {
            captureException(e);
            onError?.('Failed to load delivery profile');
        },
    });
    const upsertProfile = useUpsertDeliveryProfile(scope, { onError });

    const loaded = data?.result || null;
    const deliveryProfile = profileInScope(loaded, scope) ? loaded : null;

    return {
        deliveryProfileId,
        deliveryProfile,
        notFound: !!deliveryProfileId && !loading && !!data && !deliveryProfile,
        loadFailed: !!error && !data,
        upsertProfile,
        loading,
    };
};

export const useProfileSubregionsMutations = (deliveryProfileId, { onError } = {}) => {
    const cacheVars = { delivery_profile_id: deliveryProfileId };

    const onMutationError = (label) => (e) => {
        captureException(e);
        onError?.(`Failed to ${label}`);
    };

    const [insertSubregion, { loading: insertSubregionLoading }] = useMutation(CREATE_SUBREGION, {
        onError: onMutationError('create subregion'),
        update: (cache, { data: { result } }) => {
            cache.updateQuery({ query: GET_DELIVERY_PROFILE, variables: cacheVars }, (data) => ({
                ...(data || {}),
                result: {
                    ...(data?.result || {}),
                    subregions: [...(data?.result?.subregions || []), result],
                },
            }));
        },
    });

    const [patchSubregion, { loading: updateSubregionLoading }] = useMutation(UPDATE_SUBREGION, {
        onError: onMutationError('update subregion'),
    });

    const [removeSubregion, { loading: deleteSubregionLoading }] = useMutation(DELETE_SUBREGION, {
        onError: onMutationError('delete subregion'),
        update: (cache, { data: { removed } }) => {
            cache.updateQuery({ query: GET_DELIVERY_PROFILE, variables: cacheVars }, (data) => ({
                ...(data || {}),
                result: {
                    ...(data?.result || {}),
                    subregions: (data?.result?.subregions || []).filter((s) => s.subregion_id !== removed.subregion_id),
                },
            }));
        },
    });

    const createSubregion = useCallback(
        (subregion) =>
            insertSubregion({
                variables: { subregion: { ...subregion, delivery_profile_id: deliveryProfileId } },
            }),
        [deliveryProfileId, insertSubregion]
    );

    const updateSubregion = useCallback(
        (subregion) => {
            const { subregion_id, __typename, zips, carriers, ...rest } = subregion;
            return patchSubregion({
                variables: {
                    subregion_id,
                    update: rest,
                },
            });
        },
        [patchSubregion]
    );

    const deleteSubregion = useCallback(
        (subregion) => removeSubregion({ variables: { subregion_id: subregion.subregion_id } }),
        [removeSubregion]
    );

    const [insertZipsMutation, { loading: insertZipsLoading }] = useMutation(INSERT_ZIP_MAPPINGS, {
        onError: onMutationError('add zips'),
        update: (cache, { data: { created } }) => {
            const returned = created?.returning || [];
            if (!returned.length) return;

            cache.updateQuery({ query: GET_DELIVERY_PROFILE, variables: cacheVars }, (data) => ({
                ...(data || {}),
                result: {
                    ...(data?.result || {}),
                    subregions: mergeInsertedZips(data?.result?.subregions || [], returned),
                },
            }));
        },
    });

    const [deleteZipsMutation, { loading: deleteZipsLoading }] = useMutation(DELETE_ZIP_MAPPINGS, {
        onError: onMutationError('remove zips'),
        update: (cache, { data: { removed } }) => {
            const removedSet = (removed.returning || []).reduce((acc, z) => {
                acc[z.subregion_id] = { ...(acc[z.subregion_id] || {}), [z.zip]: true };
                return acc;
            }, {});
            cache.updateQuery({ query: GET_DELIVERY_PROFILE, variables: cacheVars }, (data) => ({
                ...(data || {}),
                result: {
                    ...(data?.result || {}),
                    subregions: (data?.result?.subregions || []).map((s) => ({
                        ...s,
                        zips: s.zips.filter((z) => !removedSet[s.subregion_id]?.[z.zip]),
                    })),
                },
            }));
        },
    });

    const saveSubregion = useSaveSubregion({
        refetchQuery: { query: GET_DELIVERY_PROFILE, variables: cacheVars },
        onError: onMutationError('save subregion'),
    });

    const [uploadMutation, { loading: uploadLoading }] = useMutation(UPLOAD_SUBREGIONS, {
        onError: onMutationError('upload subregions'),
        update: (cache, { data: { deleted, created } }) => {
            const removedIds = (deleted.returning || []).map((r) => r.subregion_id);
            cache.updateQuery({ query: GET_DELIVERY_PROFILE, variables: cacheVars }, (data) => ({
                ...(data || {}),
                result: {
                    ...(data?.result || {}),
                    subregions: [
                        ...(data?.result?.subregions || []).filter((s) => !removedIds.includes(s.subregion_id)),
                        ...(created.returning || []),
                    ],
                },
            }));
        },
    });

    const addZips = useCallback((mappings) => insertZipsMutation({ variables: { mappings } }), [insertZipsMutation]);
    const removeZips = useCallback(
        (mappings) => deleteZipsMutation({ variables: { mappings: mappings.map((m) => m.mapping_id) } }),
        [deleteZipsMutation]
    );
    const removeZip = useCallback(
        (mapping) => deleteZipsMutation({ variables: { mappings: [mapping.mapping_id] } }),
        [deleteZipsMutation]
    );
    const uploadSubregions = useCallback(
        (subregions, to_delete) => uploadMutation({ variables: { subregions, to_delete } }),
        [uploadMutation]
    );

    return {
        createSubregion,
        updateSubregion,
        deleteSubregion,
        addZips,
        removeZips,
        removeZip,
        saveSubregion,
        uploadSubregions,
        loading: {
            subregions: insertSubregionLoading || deleteSubregionLoading || uploadLoading,
            zips: insertZipsLoading || deleteZipsLoading,
        },
    };
};

export const useCarrierDirectory = (scope, deliveryProfileId) => {
    const { admin_clients } = useContext(UserContext);

    const { data: onwardData } = useQuery(QUERY_CLIENTS, { skip: scope.kind !== 'onward' });

    // Org's shipper client_ids drive the partnership-based query for Network Carriers
    // (carriers connected to the org's shippers via pricing_overrides — not in admin_clients).
    const orgShipperIds = useMemo(
        () => (admin_clients || []).filter((c) => !!c.user?.roles?.SHIPPER).map((c) => c.client_id),
        [admin_clients]
    );

    const { data: corpPricingData } = useQuery(GET_CORP_PRICING_OVERRIDES, {
        variables: { orgShipperIds, organization_id: scope.org_id },
        skip: scope.kind !== 'corp' || !scope.org_id || orgShipperIds.length === 0,
    });

    const pool = useMemo(() => {
        if (scope.kind === 'corp') {
            // Flex Centers — org-internal carriers (admin_clients with role=CARRIER).
            const flex = (admin_clients || []).filter((c) => !!c.user?.roles?.CARRIER);
            const orgIds = new Set((admin_clients || []).map((c) => c.client_id));
            const byId = new Map(flex.map((c) => [c.client_id, c]));

            // Network Carriers — external carriers connected to org shippers via
            // pricing_overrides. They live in client / partner_client / assign on each
            // override; collect unique non-org carriers across all three slots.
            (corpPricingData?.pricing_overrides || []).forEach((p) => {
                if (p.organization_id) return;
                // A contractor's pay row names the contractor as client, but it's no Final Mile carrier.
                if (p.override_type === 'CONTRACTOR') return;
                [p.client, p.partner_client, p.assign].forEach((c) => {
                    if (!c) return;
                    const roles = c.user?.roles || {};
                    if ((!roles.CARRIER && !roles.MIDDLE_MILE_CARRIER) || roles.SHIPPER) return;
                    if (orgIds.has(c.client_id)) return;
                    if (!byId.has(c.client_id)) byId.set(c.client_id, c);
                });
            });

            return Array.from(byId.values());
        }
        if (scope.kind === 'onward') {
            // Onward returns carriers + mm_carriers as separate role-keyed lists. Dedupe by client_id;
            // when a client appears in both, the `carriers` entry wins because it carries the richer
            // payload (locations), which the WH slot needs.
            const carriers = onwardData?.carriers || [];
            const mm = onwardData?.mm_carriers || [];
            const byId = new Map();
            [...mm, ...carriers].forEach((c) => byId.set(c.client_id, c));
            return Array.from(byId.values());
        }
        return [];
    }, [scope.kind, admin_clients, onwardData, corpPricingData]);

    // Keyed on the profile alone, so the cached roll-up survives the pool changing as its sources load.
    // The view only has carriers with a relevant row; carrierStatusesById fills in the rest.
    const carrierIds = useMemo(() => pool.map((c) => c.client_id), [pool]);
    const { data: statusData } = useQuery(QUERY_CARRIER_STATUSES, {
        variables: { delivery_profile_id: deliveryProfileId },
        skip: !deliveryProfileId,
        // Shows the cached roll-up at once, then refreshes it: an Apply on another profile, or a pricing
        // or integration edit elsewhere, can change it.
        fetchPolicy: 'cache-and-network',
        // A failure would otherwise leave every badge on "—", indistinguishable from loading.
        onError: (e) => captureException(e),
    });

    // `statuses` stays unset until the query lands; see statusBadge.
    const allCarriers = useMemo(() => {
        if (!statusData) return pool;
        const byId = carrierStatusesById(carrierIds, statusData.results);
        return pool.map((c) => ({ ...c, statuses: byId[c.client_id] }));
    }, [pool, carrierIds, statusData]);

    const carriersForLeg = useMemo(() => (slot) => filterCarriersByLeg(slot, allCarriers), [allCarriers]);

    return { allCarriers, carriersForLeg };
};

const QUERY_ONWARD_SHIPPERS = gql`
    query GetOnwardAdminShippers {
        results: clients(
            where: {
                user: { roles: { _has_key: "SHIPPER" } }
                approved: { _eq: true }
                deactivated: { _eq: false }
                test_acc: { _eq: false }
            }
            order_by: { business_name: asc_nulls_last }
        ) {
            client_id
            business_name
            shipper_type
        }
    }
`;

const isEligibleShipper = (c) =>
    !!c.user?.roles?.SHIPPER && c.approved === true && c.deactivated === false && c.test_acc === false;

// Identity key for a delivery_profile_assignments row under its unique
// constraint (shipper_id, order_type) — used to upsert rows into the
// assignments query cache after applying. zip_type is a mutable attribute of
// the slot, not part of its identity, so it must not be in the key: including
// it would strand a stale row when a re-apply overwrites the zip.
const assignmentKey = (a) => `${a.shipper_id}|${a.order_type}`;

export const useShipperDirectory = (scope, { onError } = {}) => {
    const { admin_clients } = useContext(UserContext);

    const { data: onwardShippers } = useQuery(QUERY_ONWARD_SHIPPERS, {
        skip: scope.kind !== 'onward',
        onError: (e) => {
            captureException(e);
            onError?.('Failed to load shippers');
        },
    });

    const shippers = useMemo(() => {
        if (scope.kind === 'corp') {
            return (admin_clients || []).filter(isEligibleShipper).filter((c) => c.shipper_type === 'AUTO_ASSIGN');
        }
        if (scope.kind === 'onward') {
            return onwardShippers?.results || [];
        }
        return [];
    }, [scope.kind, admin_clients, onwardShippers]);

    // Assignments for every profile in scope (onward: org-less profiles; corp:
    // this org's profiles), driving the "applied" badge per shipper.
    const assignmentsWhere = whereForScope(scope);
    const { data: assignmentsData } = useQuery(QUERY_DELIVERY_PROFILE_ASSIGNMENTS, {
        variables: { where: { delivery_profile: assignmentsWhere || {} } },
        skip: !assignmentsWhere,
        onError: (e) => {
            captureException(e);
            onError?.('Failed to load profile assignments');
        },
    });

    const assignmentsByShipper = useMemo(() => {
        return (assignmentsData?.results || []).reduce((acc, a) => {
            (acc[a.shipper_id] = acc[a.shipper_id] || []).push(a);
            return acc;
        }, {});
    }, [assignmentsData]);

    const [applyMutation] = useMutation(APPLY_DELIVERY_PROFILE_ASSIGNMENTS, {
        // The carrier badges roll up over the attached shippers, and the profile's auto_assignments
        // feed routedZipType.
        refetchQueries: ['GetDeliveryProfileCarrierStatuses', 'GetDeliveryProfile'],
        onError: (e) => {
            captureException(e);
            onError?.('Failed to apply profile to shipper');
        },
        update: (cache, { data: { result } }) => {
            const returned = result?.returning || [];
            if (!returned.length) return;
            const variables = { where: { delivery_profile: assignmentsWhere || {} } };
            cache.updateQuery({ query: QUERY_DELIVERY_PROFILE_ASSIGNMENTS, variables }, (data) => {
                const list = data?.results || [];
                const byKey = new Map(list.map((a) => [assignmentKey(a), a]));
                returned.forEach((a) => byKey.set(assignmentKey(a), a));
                return { ...(data || {}), results: Array.from(byKey.values()) };
            });
        },
    });

    // One mutation for every row, so a multi-shipper apply cannot land partially.
    const applyToShipper = useCallback(
        (shipperIds, deliveryProfile, orderTypes, zipType) => {
            const assignments = buildAssignmentRows({ deliveryProfile, shipperIds, zipType, orderTypes });
            if (!assignments.length) return Promise.resolve();
            return applyMutation({ variables: { assignments } });
        },
        [applyMutation]
    );

    return { shippers, assignmentsByShipper, applyToShipper };
};

// The apply bar's shipper selection and Attach action.
export const useApplyToShipper = ({
    shippers,
    deliveryProfileId,
    deliveryProfile,
    assignmentsByShipper,
    applyToShipper,
    onSuccess,
    disabled,
}) => {
    const [selectedShipperIds, setSelectedShipperIds] = useState([]);
    // Only trust the profile once it is the one selected: the id is set synchronously while the
    // profile arrives async, so until then it is the previous profile (or null).
    const loadedProfile = deliveryProfile?.delivery_profile_id === deliveryProfileId ? deliveryProfile : null;

    useEffect(() => {
        setSelectedShipperIds([]);
    }, [deliveryProfileId]);

    // Keyed on the joined values: the array is a new reference on every cache write.
    const defaultOrderTypesKey = (loadedProfile?.default_order_types || []).join('|');
    const orderTypes = useMemo(
        () => applyOrderTypesFor(loadedProfile),
        [loadedProfile?.delivery_profile_id, defaultOrderTypesKey]
    );

    // What the profile's other shippers route on, so a new shipper joins them rather than taking a
    // stale profile default.
    const zipType = routedZipType(loadedProfile);

    const [applying, setApplying] = useState(false);
    // Loaded, not merely selected: the id is set synchronously while the profile arrives async.
    // Applying in that window would write the wrong zip_type and order types to every row.
    const canApply =
        selectedShipperIds.length > 0 &&
        !!deliveryProfileId &&
        !!zipType &&
        orderTypes.length > 0 &&
        !disabled &&
        !applying;

    // A profile is "applied" to a shipper if any assignment row links them —
    // regardless of which order_type(s) it covers.
    const isApplied = (s) =>
        !!s && (assignmentsByShipper?.[s.client_id] || []).some((a) => a.delivery_profile_id === deliveryProfileId);

    // True when EVERY selected shipper is applied.
    const selectedShipperApplied = useMemo(
        () =>
            selectedShipperIds.length > 0 &&
            selectedShipperIds.every((id) => isApplied(shippers.find((sh) => sh.client_id === id))),
        [shippers, selectedShipperIds, deliveryProfileId, assignmentsByShipper]
    );

    const shipperOptions = useMemo(
        () => shippers.map((s) => ({ value: s.client_id, label: s.business_name })),
        [shippers]
    );

    const apply = async () => {
        setApplying(true);
        try {
            // No `|| 'DROPOFF'` fallback: canApply guarantees the zip type, and a default would
            // write a zip type the shippers don't route on.
            const res = await applyToShipper(selectedShipperIds, loadedProfile, orderTypes, zipType);
            // Only report success when a row came back: the mutation swallows failures via onError,
            // so an unconditional call here would claim success on a failed apply.
            if (res?.data?.result?.returning?.length) onSuccess?.();
        } finally {
            setApplying(false);
        }
    };

    return { selectedShipperIds, setSelectedShipperIds, shipperOptions, selectedShipperApplied, canApply, apply };
};

export const useSubregionImportExport = ({
    subregions,
    deliveryProfile,
    deliveryProfileId,
    isOnwardAdmin,
    carriers,
    uploadSubregions,
    onError,
}) => {
    const COLUMNS = useColumns({ isOnwardAdmin });

    const importSubregions = useCallback(
        (headers, body) => {
            try {
                const { to_add, to_remove } = parseImportedSubregions(
                    headers,
                    body,
                    {
                        deliveryProfileId,
                        isOnwardAdmin,
                        carriersById: carriers.carriersById,
                        mmCarriersById: carriers.mmCarriersById,
                        warehouseCarriersById: carriers.warehouseCarriersById,
                    },
                    subregions
                );
                uploadSubregions(to_add, to_remove);
            } catch (e) {
                captureException(e);
                onError?.(`Error importing subregions: ${e.message}`);
            }
        },
        [subregions, deliveryProfileId, isOnwardAdmin, carriers, uploadSubregions, onError]
    );

    const exportSubregions = useCallback(() => {
        const rows = serializeSubregionsForExport(subregions, {
            genName: (s, i) => s.name || `Subregion ${i + 1}`,
        });
        const enrichments = {
            carriersById: carriers.carriersById,
            mmCarriersById: carriers.mmCarriersById,
            warehouseCarriersById: carriers.warehouseCarriersById,
        };
        csvDownload({
            headers: COLUMNS.map((col) => col.header),
            data: rows.map((row) => COLUMNS.map((col) => col.value(row, enrichments))),
            filename: `${deliveryProfile?.name}-${format(new Date(), 'yyyy-MM-dd')}.csv`,
            delimiter: ',',
        });
    }, [subregions, deliveryProfile, carriers, COLUMNS]);

    return { importSubregions, exportSubregions };
};

// Each carrier's region zips, for the map's hover overlay. Loaded for all the subregion's carriers up
// front so a hover never waits on the network. A carrier is absent until the query lands, then maps to
// its zips (empty when it has configured none).
export const useCarrierRegions = (carrierIds = []) => {
    // Keyed on the ids' text: callers rebuild the array every render.
    const key = [...new Set(carrierIds.filter(Boolean))].sort().join('|');
    const ids = useMemo(() => (key ? key.split('|') : []), [key]);
    const { data } = useQuery(QUERY_CARRIER_REGIONS, {
        variables: { carrier_ids: ids },
        skip: !ids.length,
        onError: captureException,
    });

    return useMemo(() => (data ? carrierRegionZips(ids, data.results) : {}), [ids, data]);
};
